import { isPlatformBrowser } from '@angular/common';
import { Component, inject, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

import { NotificationCardComponent } from '@rpgc-ai/ui/notification-card/notification-card.component';

import { OverlayComponent } from '@rpgc-ai/blocks/overlay/overlay.component';

import { SessionService } from '@rpgc-ai/services/auth/session.service';
import { ChatsService } from '@rpgc-ai/services/chats/chats.service';
import { NotificationsService } from '@rpgc-ai/services/notifications/notifications.service';

import { filter, map } from 'rxjs/operators';

const GOOGLE_ANALYTICS_TAG_ID = 'G-4Y9XNNQC0L';
const GOOGLE_ADS_TAG_ID = 'AW-17995155273';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

@Component({
  selector: 'app-root',
  styleUrl: './app.component.scss',
  templateUrl: './app.component.html',
  imports: [RouterModule, OverlayComponent, NotificationCardComponent],
})
export class AppComponent {
  readonly #platformId = inject(PLATFORM_ID);
  readonly #router = inject(Router);
  readonly #title = inject(Title);
  readonly #isBrowser = isPlatformBrowser(this.#platformId);

  readonly #sessionService = inject(SessionService);
  readonly #notificationsService = inject(NotificationsService);
  readonly #chatsService = inject(ChatsService);
  // Variables
  readonly notifications = this.#notificationsService.notifications;
  #lastTrackedPagePath: string | null = null;

  constructor() {
    // получаем токен
    this.#sessionService.loadSession.set(true);
    this.#chatsService.loadChats.set(true);
    this.#initGoogleTagRouteTracking();
  }

  // Methods
  removeNotification(id: string): void {
    this.#notificationsService.removeNotification(id);
  }

  #initGoogleTagRouteTracking(): void {
    if (!this.#isBrowser) {
      return;
    }

    this.#trackGooglePageView(this.#router.url || window.location.pathname + window.location.search + window.location.hash);

    this.#router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(({ urlAfterRedirects }) => urlAfterRedirects),
        takeUntilDestroyed(),
      )
      .subscribe((url) => this.#trackGooglePageView(url));
  }

  #trackGooglePageView(pagePath: string): void {
    if (!pagePath || pagePath === this.#lastTrackedPagePath || !window.gtag) {
      return;
    }

    this.#lastTrackedPagePath = pagePath;

    const pageLocation = window.location.href;
    const pageTitle = this.#title.getTitle() || document.title;

    window.gtag?.('event', 'page_view', {
      send_to: GOOGLE_ANALYTICS_TAG_ID,
      page_location: pageLocation,
      page_title: pageTitle,
    });

    window.gtag?.('event', 'page_view', {
      send_to: GOOGLE_ADS_TAG_ID,
    });
  }
}
